import { useRef, useState, useEffect } from "react";
import Errors from "./Errors";
import getStyles from "./styles";
import Instructions from "./Instructions";
import Confirmations from "./Confirmations";
import MobileControls from "./MobileControls";
import DesktopControls from "./DesktopControls";
import DocumentBackPopup from "./DocumentBackPopup";
import type { IImageCaptureProps } from "../../types";
import { useAppDispatch, useAppSelector } from "../../redux";
import { selectBirthDate, selectRegistrationNumber } from "../RegistrationPage/RegistrationPageSlice";
import { selectRequestId, selectRequestSource, selectIsAuthentication, setError, setIsLoading } from "../../AppSlice";
import {
  registerAction,
  selectAttempts,
  setPaneemError,
  incrementAttempts,
  selectPaneemError,
  authenticateAction,
  setAskDocumentBack,
  selectAskDocumentBack,
} from "./ImageCaptureSlice";
import Webcam from "react-webcam";
import { Box, Modal } from "@mui/material";
import { BrowserView, MobileView } from "react-device-detect";
import RegulaImageCapture from "./RegulaImageCapture";

export default function _ImageCapture(props: IImageCaptureProps) {
  const { isOpen, text, email, phoneNumber, isSMS, close } = props;

  const styles = getStyles();

  const dispatch = useAppDispatch();

  const isAuthentication = useAppSelector(selectIsAuthentication);
  const paneemError = useAppSelector(selectPaneemError);
  const attempts = useAppSelector(selectAttempts);
  const askDocumentBack = useAppSelector(selectAskDocumentBack);
  const registrationNumber = useAppSelector(selectRegistrationNumber);
  const birthDate = useAppSelector(selectBirthDate);
  const requestId = useAppSelector(selectRequestId);
  const requestSource = useAppSelector(selectRequestSource);

  const webcamRef = useRef<Webcam>(null);

  const [mode, setMode] = useState<VideoFacingModeEnum>("user");
  const [selfie, setSelfie] = useState("");
  const [isSelfieApproved, setIsSelfieApproved] = useState(false);
  const [document, setDocument] = useState("");
  const [isDocumentApproved, setIsDocumentApproved] = useState(false);
  const [documentBack, setDocumentBack] = useState("");
  const [isDocumentBackApproved, setIsDocumentBackApproved] = useState(false);
  const [isDocumentBack, setIsDocumentBack] = useState<boolean | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(true);
  const [isContinueDisabled, setIsContinueDisabled] = useState(true);
  const [isGreeted, setIsGreeted] = useState(false);
  const [isOverride, setIsOverride] = useState(false);

  useEffect(() => {
    if (isContinueDisabled) setTimeout(() => setIsContinueDisabled(false), 1500);
  }, [isContinueDisabled]);

  useEffect(() => {
    if (askDocumentBack && isDocumentBack === false) {
      dispatch(setPaneemError(1));
    }
  }, [askDocumentBack]);

  useEffect(() => {
    if (attempts >= 2)
      setTimeout(() => {
        dispatch(setIsLoading(false));

        setIsOverride(true);
      }, 500);
  }, [attempts]);

  const handleChangeCameraMode = () => setMode(mode === "user" ? "environment" : "user");

  const handleCapture = () => {
    if (!webcamRef.current) return;
    setIsContinueDisabled(true);

    const src = webcamRef.current.getScreenshot() as string;

    if (!selfie) {
      setMode("environment");
      return setSelfie(src.replace("data:image/jpeg;base64,", ""));
    }

    if (!document) return setDocument(src.replace("data:image/jpeg;base64,", ""));

    if (!documentBack) setDocumentBack(src.replace("data:image/jpeg;base64,", ""));
  };

  const handleContinue = async () => {
    if (!isSelfieApproved && isAuthentication) {
      if (!registrationNumber || !birthDate) return;

      setIsSelfieApproved(true);

      await dispatch(
        authenticateAction({
          registrationNumber,
          birthDate,
          email,
          phoneNumber,
          isSMS,
          language: text.language,
          attempts,
          selfie,
          requestId,
          requestSource,
        })
      );

      return;
    }

    if (!isSelfieApproved && !isAuthentication) {
      setIsSelfieApproved(true);
      setIsDialogOpen(true);
      setIsContinueDisabled(true);

      return;
    }

    if (!isDocumentApproved && !isDocumentBack) {
      if (!registrationNumber || !birthDate) return;

      setIsDocumentApproved(true);

      await dispatch(
        registerAction({
          registrationNumber,
          birthDate,
          email,
          phoneNumber,
          isSMS,
          language: text.language,
          attempts,
          selfie,
          documentFront: document,
          documentBack,
          requestId,
          requestSource,
          isDocumentBack,
        })
      );

      return;
    }

    if (!isDocumentApproved && isDocumentBack) {
      setIsDocumentApproved(true);
      setIsDialogOpen(true);
      setIsContinueDisabled(true);

      return;
    }

    if (!isDocumentBackApproved) {
      if (!registrationNumber || !birthDate) return;

      setIsDocumentBackApproved(true);

      await dispatch(
        registerAction({
          registrationNumber,
          birthDate,
          email,
          phoneNumber,
          isSMS,
          language: text.language,
          attempts,
          selfie,
          documentFront: document,
          documentBack,
          requestId,
          requestSource,
          isDocumentBack,
        })
      );
    }
  };

  const handleRetake = () => {
    if (!isSelfieApproved) setSelfie("");

    if (!isDocumentApproved) setDocument("");

    if (!isDocumentBackApproved) setDocumentBack("");

    setMode(!isSelfieApproved ? "user" : "environment");

    setIsDialogOpen(true);
    setIsContinueDisabled(true);
  };

  const handleNoDocumentBack = () => {
    setIsDocumentBack(false);
    setIsContinueDisabled(true);

    dispatch(setPaneemError(1));
    dispatch(setAskDocumentBack(false));
  };

  const handleDocumentBack = () => {
    setIsDocumentBack(true);
    setIsContinueDisabled(true);
    setIsDialogOpen(true);
  };

  const handleResetAttempt = () => {
    if (isDocumentBack === null) setIsDocumentBack(false);

    dispatch(setPaneemError(null));
    dispatch(incrementAttempts());

    resetImages();
  };

  const handleCloseDialog = () => {
    if (isGreeted) {
      setIsDialogOpen(false);
    } else {
      setIsContinueDisabled(true);
      setIsGreeted(true);
    }
  };

  const handleClose = () => {
    resetImages();

    close();
  };

  const resetImages = () => {
    setSelfie("");
    setIsSelfieApproved(false);
    setDocument("");
    setIsDocumentApproved(false);
    setDocumentBack("");
    setIsDocumentBackApproved(false);
    setIsDialogOpen(attempts + 1 < 2 ? false : true);
    setIsContinueDisabled(true);
    setMode("user");
  };

  const handleNoCamera = () => {
    dispatch(setError(text.error_finding_camera));
    handleClose();
  };

  return (
    <Modal
      open={isOpen}
      closeAfterTransition
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <>
        {(selfie && !isSelfieApproved) ||
        (document && !isDocumentApproved) ||
        (documentBack && !isDocumentBackApproved) ||
        (selfie && isAuthentication) ||
        (selfie && document && !isDocumentBack) ||
        (selfie && document && documentBack) ? null : !selfie && !isOverride ? (
          <RegulaImageCapture
            isOpen={isOpen}
            setSelfie={setSelfie}
            handleContinue={handleContinue}
            close={close}
            setIsDialogOpen={setIsDialogOpen}
            text={text}
            attempts={attempts}
          />
        ) : (
          <Box sx={styles.webcamWrapper}>
            <Webcam
              onUserMediaError={handleNoCamera}
              screenshotQuality={1}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={{
                width: 920,
                facingMode: mode,
              }}
              style={
                mode === "user"
                  ? {
                      WebkitTransform: "scaleX(-1)",
                      transform: "scaleX(-1)",
                      height: "100%",
                      width: "auto",
                    }
                  : { height: "100%", width: "auto" }
              }
            />

            <Box sx={selfie ? styles.documentGuideline : styles.selfieGuideline} />

            <BrowserView style={{ display: "flex", justifyContent: "center" }}>
              <DesktopControls
                buttonText={text.capture}
                styles={styles}
                handleClose={handleClose}
                handleCapture={handleCapture}
              />
            </BrowserView>

            <MobileView style={{ display: "flex", justifyContent: "center", flex: 1 }}>
              <MobileControls
                styles={styles}
                handleChangeCameraMode={handleChangeCameraMode}
                handleCapture={handleCapture}
                handleClose={handleClose}
              />
            </MobileView>
          </Box>
        )}

        <Instructions
          isDialogOpen={isDialogOpen}
          selfie={!!selfie}
          document={!!document}
          text={text}
          disabled={isContinueDisabled}
          isGreeted={isGreeted}
          styles={styles}
          handleCloseDialog={handleCloseDialog}
          attempts={attempts}
        />

        <Confirmations
          text={text}
          disabled={isContinueDisabled}
          selfie={selfie}
          isAuthentication={isAuthentication}
          isDocumentBack={isDocumentBack}
          isSelfieApproved={isSelfieApproved}
          document={document}
          isDocumentApproved={isDocumentApproved}
          documentBack={documentBack}
          isDocumentBackApproved={isDocumentBackApproved}
          styles={styles}
          retake={handleRetake}
          proceed={handleContinue}
        />

        <Errors
          text={text}
          disabled={isContinueDisabled}
          paneemError={paneemError}
          isAuthentication={isAuthentication}
          styles={styles}
          handleResetAttempt={handleResetAttempt}
        />

        <DocumentBackPopup
          text={text}
          askDocumentBack={askDocumentBack}
          isDocumentBack={isDocumentBack}
          styles={styles}
          handleNoDocumentBack={handleNoDocumentBack}
          handleDocumentBack={handleDocumentBack}
        />
      </>
    </Modal>
  );
}
